/**
 * What the profile editor can offer, and the worker inventory it comes from.
 *
 * Each worker summarises the `.enc` profiles it has installed and sends that
 * summary with its session heartbeat. The cloud merges the recent summaries
 * with the static fallback in `catalog/botbrowser.ts` and serves the result at
 * `GET /v1/profiles/catalog`. Worker counts are advisory: a combination no
 * online worker has is still a valid profile, it just cannot launch yet.
 */

import { z } from "zod";
import { ArchSchema, OsSchema, WindowsVersionSchema } from "./primitives.ts";

// ============ Worker inventory (local-api -> saas-api) ============

/** One distinct kind of installed `.enc` profile, with how many files match. */
export const WorkerInventoryEntrySchema = z
	.object({
		major: z.number().int().positive().describe("Browser major version"),
		os: OsSchema,
		osVersion: z
			.string()
			.max(64)
			.describe("Raw platformVersion from the profile (e.g. 15.0.0)"),
		windowsVersion: WindowsVersionSchema.optional().describe(
			"Normalised Windows version, Windows profiles only",
		),
		arch: ArchSchema,
		gpuName: z
			.string()
			.max(128)
			.describe("Simplified GPU name (e.g. RTX-4060)"),
		renderer: z.string().max(512).describe("Full unmasked WebGL renderer"),
		model: z.string().max(64).optional().describe("Device model, Android only"),
		count: z.number().int().min(1).describe("Profile files of this kind"),
	})
	.describe("Installed profile kind on a worker");

export type WorkerInventoryEntry = z.infer<typeof WorkerInventoryEntrySchema>;

export const WorkerInventorySchema = z
	.object({
		browserMajors: z
			.array(z.number().int().positive())
			.max(200)
			.describe("Installed browser majors"),
		entries: z.array(WorkerInventoryEntrySchema).max(20_000),
	})
	.describe("A worker's installed browsers and profile kinds");

export type WorkerInventory = z.infer<typeof WorkerInventorySchema>;

// ============ Catalog (saas-api -> web) ============

export const CatalogSourceSchema = z
	.enum(["inventory", "static"])
	.describe(
		"inventory: seen on a worker in the last 24h; static: built-in fallback",
	);

export type CatalogSource = z.infer<typeof CatalogSourceSchema>;

const WorkersSchema = z
	.number()
	.int()
	.min(0)
	.describe("Recently seen workers that can launch this option");

const MajorsSchema = z
	.array(z.number().int().positive())
	.optional()
	.describe("Browser majors this option is installed for (inventory only)");

export const ProfileCatalogBrowserSchema = z.object({
	major: z.number().int().positive(),
	workers: WorkersSchema,
	source: CatalogSourceSchema,
});

export const ProfileCatalogOsVersionSchema = z.object({
	/** What goes in a profile's `osVersion`: "10"/"11" on Windows, a major elsewhere. */
	value: z.string(),
	label: z.string(),
	workers: WorkersSchema,
	source: CatalogSourceSchema,
	majors: MajorsSchema,
});

export const ProfileCatalogOsSchema = z.object({
	os: OsSchema,
	label: z.string(),
	versions: z.array(ProfileCatalogOsVersionSchema),
});

export const ProfileCatalogAndroidModelSchema = z.object({
	model: z.string(),
	osVersion: z.string().optional().describe("Android major, e.g. 14"),
	workers: WorkersSchema,
	majors: MajorsSchema,
});

export const ProfileCatalogGpuSchema = z.object({
	os: OsSchema,
	gpuName: z.string(),
	renderer: z.string().optional(),
	workers: WorkersSchema,
	majors: MajorsSchema,
});

export const ProfileCatalogSchema = z
	.object({
		browsers: z
			.array(ProfileCatalogBrowserSchema)
			.describe("Browser majors, newest first"),
		oses: z.array(ProfileCatalogOsSchema),
		androidModels: z.array(ProfileCatalogAndroidModelSchema),
		gpus: z.array(ProfileCatalogGpuSchema),
		totalWorkers: z
			.number()
			.int()
			.min(0)
			.describe("Workers seen in the last 24h"),
		generatedAt: z.string().describe("ISO 8601"),
	})
	.describe("Launchable profile options");

export type ProfileCatalogBrowser = z.infer<typeof ProfileCatalogBrowserSchema>;
export type ProfileCatalogOsVersion = z.infer<
	typeof ProfileCatalogOsVersionSchema
>;
export type ProfileCatalogOs = z.infer<typeof ProfileCatalogOsSchema>;
export type ProfileCatalogAndroidModel = z.infer<
	typeof ProfileCatalogAndroidModelSchema
>;
export type ProfileCatalogGpu = z.infer<typeof ProfileCatalogGpuSchema>;
export type ProfileCatalog = z.infer<typeof ProfileCatalogSchema>;

/** How many recently seen workers can launch a saved profile. */
export const LaunchableHintSchema = z
	.object({
		workers: z.number().int().min(0),
		total: z.number().int().min(0),
	})
	.describe("Advisory: workers that can launch this profile, of those seen");

export type LaunchableHint = z.infer<typeof LaunchableHintSchema>;

// ============ Session heartbeat (local-api -> saas-api) ============

/** Worker's own view of which sessions it is still running. */
export const SessionHeartbeatSchema = z
	.object({
		machineId: z
			.string()
			.min(1)
			.max(255)
			.describe("Worker machine identifier, as sent at authorize time"),
		sessionIds: z
			.array(z.string().min(1).max(255))
			.max(1000)
			.describe(
				"Local session ids the worker is still running. Authoritative for this machine: anything else attributed to it is released.",
			),
		inventoryHash: z
			.string()
			.min(1)
			.max(128)
			.optional()
			.describe("Hash of the worker's current inventory, sent every time"),
		inventory: WorkerInventorySchema.optional().describe(
			"Full inventory, sent when the hash changed or the cloud asked for it",
		),
	})
	.describe("Session liveness report");

export type SessionHeartbeat = z.infer<typeof SessionHeartbeatSchema>;

export const SessionHeartbeatResultSchema = z
	.object({
		refreshed: z.number().int().min(0),
		released: z.number().int().min(0),
		profilesDeleted: z.number().int().min(0),
		current: z
			.number()
			.int()
			.min(0)
			.describe("Organization concurrency after the reconcile"),
		inventoryStale: z
			.boolean()
			.optional()
			.describe(
				"The cloud has no inventory for this hash; send the full inventory next time",
			),
	})
	.describe("Session liveness report result");

export type SessionHeartbeatResult = z.infer<
	typeof SessionHeartbeatResultSchema
>;
